import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash.debounce';
import Box from '@mui/material/Box';
import { mapKitchenApiToRow } from '../../context-api/api-services/StoreManagement.service';
import { useStoreManagementData } from '../../context-api/store-management/StoreManagementDataContext';
import { getManagingBranchNameForApi } from '../../utils/managingBranchName';
import {
  getKitchenListPageCache,
  invalidateKitchenListPageCache,
  setKitchenListPageCache,
} from '../../utils/kitchenListPageCache';
import {
  COLORS,
  STATUS_FILTER_ALL,
  STORE_SEARCH_DEBOUNCE_MS,
  STORE_VISIBILITY_FILTER_ALL,
} from './storeManagementConstants';
import { StoreManagementHeader } from './StoreManagementHeader';
import { StoreManagementFilterBar } from './StoreManagementFilterBar';
import { StoreManagementStoresTable } from './StoreManagementStoresTable';
import { StoreManagementRowMenu } from './StoreManagementRowMenu';
import { StoreManagementUpdateStatusModal } from './StoreManagementUpdateStatusModal';
import { setStoreDetailsCache } from './store-details/storeDetailsCache';

const StoreManagement = () => {
  const navigate = useNavigate();
  const {
    kitchensByManagingBranchData,
    fetchKitchensByManagingBranch,
    markKitchensListSatisfiedWithoutFetch,
  } = useStoreManagementData();

  const [rows, setRows] = useState([]);
  const [totalKitchens, setTotalKitchens] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loadError, setLoadError] = useState(null);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);

  const [storeNameSearch, setStoreNameSearch] = useState('');
  const [debouncedKitchenSearch, setDebouncedKitchenSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTER_ALL);
  const [storeVisibilityFilter, setStoreVisibilityFilter] = useState(STORE_VISIBILITY_FILTER_ALL);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);
  const [statusModalRow, setStatusModalRow] = useState(null);
  const [listRefreshNonce, setListRefreshNonce] = useState(0);

  const skipSearchPageResetRef = useRef(true);

  const loading = kitchensByManagingBranchData.loading;

  const listCacheFilters = useMemo(
    () => ({
      kitchen_name_search: debouncedKitchenSearch,
      statusFilter,
      storeVisibilityFilter,
    }),
    [debouncedKitchenSearch, statusFilter, storeVisibilityFilter]
  );

  const commitDebouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setDebouncedKitchenSearch(String(value ?? '').trim());
      }, STORE_SEARCH_DEBOUNCE_MS),
    []
  );

  useEffect(() => {
    commitDebouncedSearch(storeNameSearch);
    return () => {
      commitDebouncedSearch.cancel();
    };
  }, [storeNameSearch, commitDebouncedSearch]);

  useEffect(() => {
    if (skipSearchPageResetRef.current) {
      skipSearchPageResetRef.current = false;
      return;
    }
    setPage(1);
  }, [debouncedKitchenSearch]);

  useEffect(() => {
    const branch = getManagingBranchNameForApi();
    const cached = getKitchenListPageCache(branch, page, limit, listCacheFilters);
    if (cached) {
      markKitchensListSatisfiedWithoutFetch();
      setRows(cached.rows);
      setTotalKitchens(cached.totalKitchens);
      setLastRefreshAt(cached.refreshedAt);
      setLoadError(null);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoadError(null);

      const statusForApi =
        statusFilter === 'Active' ? true : statusFilter === 'Inactive' ? false : undefined;
      const storeVisibilityForApi =
        storeVisibilityFilter === STORE_VISIBILITY_FILTER_ALL ? undefined : storeVisibilityFilter;

      const res = await fetchKitchensByManagingBranch({
        managing_branch_name: branch,
        page,
        limit,
        kitchen_name_search: debouncedKitchenSearch || undefined,
        status: statusForApi,
        store_visibility_status: storeVisibilityForApi,
      });
      if (cancelled) return;
      if (res.hasError) {
        setLoadError(res.message || 'Failed to load kitchens.');
        setRows([]);
        setTotalKitchens(0);
        setLastRefreshAt(null);
      } else {
        const payload = res.data || {};
        if (payload.success !== true) {
          setLoadError('Could not load store list.');
          setRows([]);
          setTotalKitchens(0);
          setLastRefreshAt(null);
        } else {
          const list = Array.isArray(payload.data) ? payload.data : [];
          const total =
            typeof payload.total_kitchens === 'number' ? payload.total_kitchens : list.length;
          const mapped = list.map((item, i) => mapKitchenApiToRow(item, i));
          const refreshedAt = Date.now();
          setKitchenListPageCache(
            branch,
            page,
            limit,
            { rows: mapped, totalKitchens: total, refreshedAt },
            listCacheFilters
          );
          setTotalKitchens(total);
          setRows(mapped);
          setLastRefreshAt(refreshedAt);
          setLoadError(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    page,
    limit,
    debouncedKitchenSearch,
    statusFilter,
    storeVisibilityFilter,
    listRefreshNonce,
    listCacheFilters,
    fetchKitchensByManagingBranch,
    markKitchensListSatisfiedWithoutFetch,
  ]);

  const handleRefreshList = () => {
    const branch = getManagingBranchNameForApi();
    invalidateKitchenListPageCache(branch, page, limit, listCacheFilters);
    setListRefreshNonce((n) => n + 1);
  };

  const menuRow = useMemo(() => rows.find((r) => r.id === menuRowId), [rows, menuRowId]);

  const openMenu = (id) => (e) => {
    setMenuAnchor(e.currentTarget);
    setMenuRowId(id);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRowId(null);
  };

  const handleViewStore = (row) => {
    if (!row?.id || !row?.rawData) return;
    setStoreDetailsCache(row.id, row.rawData);
    navigate(`/storemanagement/${row.id}`, { state: { store: row.rawData } });
  };

  const handleOpenUpdateStatus = (row) => {
    if (!row) return;
    setStatusModalRow(row);
  };

  const handleCloseUpdateStatus = () => {
    setStatusModalRow(null);
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        bgcolor: '#f1f5f9',
        py: { xs: 2, sm: 3 },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 1070,
          mx: 'auto',
          bgcolor: COLORS.white,
          borderRadius: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 32px rgba(63,63,110,0.10)',
          overflow: 'hidden',
        }}
      >
        <StoreManagementHeader
          totalKitchens={totalKitchens}
          lastRefreshAt={lastRefreshAt}
          loading={loading}
          onRefreshList={handleRefreshList}
        />
        <StoreManagementFilterBar
          storeNameSearch={storeNameSearch}
          onStoreNameSearchChange={setStoreNameSearch}
          statusFilter={statusFilter}
          onStatusFilterChange={(value) => {
            setStatusFilter(value);
            setPage(1);
          }}
          storeVisibilityFilter={storeVisibilityFilter}
          onStoreVisibilityFilterChange={(value) => {
            setStoreVisibilityFilter(value);
            setPage(1);
          }}
        />
        <StoreManagementStoresTable
          loadError={loadError}
          loading={loading}
          rows={rows}
          page={page}
          limit={limit}
          totalKitchens={totalKitchens}
          onPageChange={setPage}
          onLimitChange={(next) => {
            setLimit(next);
            setPage(1);
          }}
          openMenu={openMenu}
          onViewStore={handleViewStore}
        />
      </Box>

      <StoreManagementRowMenu
        menuAnchor={menuAnchor}
        menuRow={menuRow}
        onClose={closeMenu}
        onViewStore={handleViewStore}
        onUpdateStatus={handleOpenUpdateStatus}
      />

      <StoreManagementUpdateStatusModal
        open={Boolean(statusModalRow)}
        row={statusModalRow}
        onClose={handleCloseUpdateStatus}
        onSuccess={handleRefreshList}
      />
    </Box>
  );
};

export default StoreManagement;
